import { useMemo, useState } from "react";
import { ChevronDown, Search, X, Check } from "lucide-react";
import { cn } from "../../../../utils/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../components/UI/Popover";
import { eventTypeChipColor } from "../../utils/eventType";

/**
 * Multi-select popover for filtering logs by chatbot_setting.
 * Trigger is a pill that summarizes the selection.
 */
const BotMultiSelect = ({ bots = [], eventTypes = [], value = [], onChange }) => {
  const [query, setQuery] = useState("");
  const selected = new Set(value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return bots;
    return bots.filter((b) => {
      const label = (b.description || "").toLowerCase();
      const et = (b.eventType || "").toLowerCase();
      return label.includes(q) || et.includes(q);
    });
  }, [bots, query]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  };

  const clearAll = () => {
    onChange([]);
  };

  const activeCount = value.length;
  const label =
    activeCount === 0
      ? "Tất cả bot"
      : activeCount === 1
        ? bots.find((b) => b.id === value[0])?.description?.slice(0, 24) ||
          "1 bot đã chọn"
        : `${activeCount} bot đã chọn`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md ring-1 transition-colors",
            activeCount > 0
              ? "bg-indigo-50 text-indigo-700 ring-indigo-200 hover:bg-indigo-100"
              : "bg-white text-gray-700 ring-gray-200 hover:bg-gray-50",
          )}
          title="Lọc theo bot"
        >
          <span className="max-w-[180px] truncate">{label}</span>
          <ChevronDown size={12} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="px-2.5 pt-2.5 pb-2 border-b border-gray-100">
          <div className="relative">
            <Search
              size={13}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Tìm bot theo mô tả..."
              className="w-full pl-8 pr-8 py-1.5 text-sm rounded-md border border-gray-200 bg-white focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 outline-none transition"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        <div className="max-h-72 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-gray-400">
              Không có bot nào khớp.
            </div>
          ) : (
            filtered.map((bot) => {
              const checked = selected.has(bot.id);
              const color = eventTypeChipColor(bot.eventType);
              const eventLabel =
                eventTypes.find((e) => e.action === bot.eventType)?.actionName ||
                bot.eventType ||
                "—";
              return (
                <button
                  key={bot.id}
                  type="button"
                  onClick={() => toggle(bot.id)}
                  className={cn(
                    "w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-gray-50 transition-colors",
                    checked && "bg-indigo-50/50",
                  )}
                >
                  <span
                    className={cn(
                      "w-4 h-4 shrink-0 rounded border flex items-center justify-center",
                      checked
                        ? "bg-indigo-600 border-indigo-600"
                        : "bg-white border-gray-300",
                    )}
                  >
                    {checked && <Check size={11} className="text-white" />}
                  </span>
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ backgroundColor: color }}
                  />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-gray-900 truncate">
                      {bot.description || (
                        <span className="italic text-gray-400">
                          (Chưa có mô tả)
                        </span>
                      )}
                    </span>
                    <span className="block text-[11px] text-gray-500 truncate">
                      {eventLabel}
                    </span>
                  </span>
                </button>
              );
            })
          )}
        </div>

        {activeCount > 0 && (
          <div className="px-2.5 py-2 border-t border-gray-100 flex items-center justify-between">
            <span className="text-[11px] text-gray-500">
              {activeCount} bot đã chọn
            </span>
            <button
              type="button"
              onClick={clearAll}
              className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
            >
              Xóa chọn
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default BotMultiSelect;
